import Address1UnitNoZipCodeUtils from '@/models/common/Address1UnitNoZipCodeUtils';
import IAddress1UnitNoZipCode from '@/models/common/IAddress1UnitNoZipCode';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import ISalesHistoryProperty from '@/models/sales-history/ISalesHistoryProperty';
import ISalesHistorySelections from '@/models/sales-history/ISalesHistorySelections';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

// Internal structures - payload types for actions
//
interface IPropertyItemIndexed {
    index: number;
    propertyItem: ISalesHistoryProperty;
}

interface ISelectionsItemIndexed {
    index: number;
    selections: ISalesHistorySelections;
}

// Internal only: The set of mutation method names as strings.
// Actions call mutations via this.context.commit() with the first argument as the mutation method name.
const Mutate = {
    addProperty: 'addProperty',
    replaceProperty: 'replaceProperty',
    deleteProperty: 'deleteProperty',
    addSelections: 'addSelections',
    replaceSelections: 'replaceSelections',
    deleteSelections: 'deleteSelections'
};

@Module({ namespaced: true, name: 'salesHistoryV36' })
export default class SalesHistoryStoreModuleV36 extends VuexModule {
    // Private are the store properties
    private salesHistoryProperties: ISalesHistoryProperty[] = [];
    private salesHistorySelectionsArray: ISalesHistorySelections[] = [];

    /**
     * Get the array of sales history properties
     */
    get SalesHistoryProperties(): ISalesHistoryProperty[] {
        return this.salesHistoryProperties;
    }

    /**
     * Get a sales history property by the given address1, unit number and zip code.
     * Returns null when one does not exist.
     */
    get SalesHistoryByAddress1UnitNoZipCode() {
        return (compAddress: ICompAddress): ISalesHistoryProperty | null => {
            if (this.salesHistoryProperties.length > 0) {
                const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
                for (const salesHistoryProperty of this.salesHistoryProperties) {
                    if (Address1UnitNoZipCodeUtils.matches(salesHistoryProperty.address1UnitNoZipCode, address1UnitNoZipCode)) {
                        return salesHistoryProperty;
                    }
                }
            }
            return null;
        };
    }

    /**
     * Get the count of sales history properties
     */
    get PropertyCount(): number {
        return this.salesHistoryProperties.length;
    }

    /**
     * Get the array of sales history selections
     */
    get SalesHistorySelections(): ISalesHistorySelections[] {
        return this.salesHistorySelectionsArray;
    }

    /**
     * Get a sales history selections by the given address1, unit number and zip code.
     * Returns null when one does not exist.
     */
    get SelectionsByAddress1UnitNoZipCode() {
        return (compAddress: ICompAddress): ISalesHistorySelections | null => {
            if (this.salesHistorySelectionsArray.length > 0) {
                const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
                for (const salesHistorySelections of this.salesHistorySelectionsArray) {
                    if (Address1UnitNoZipCodeUtils.matches(salesHistorySelections.address1UnitNoZipCode, address1UnitNoZipCode)) {
                        return salesHistorySelections;
                    }
                }
            }
            return null;
        };
    }

    /**
     * Get the count of sales history selections
     */
    get SelectionsCount(): number {
        return this.salesHistorySelectionsArray.length;
    }

    /**
     * Action: Add or replace the given sales history property using the given sales history property.
     * @param property
     * @returns true on add, false on replace
     */
    @Action
    async addOrReplaceProperty(property: ISalesHistoryProperty): Promise<boolean> {
        // Find any existing with the same address1, unit number and zip code.
        let foundIdx = -1;
        for (const propertyIdx in this.salesHistoryProperties) {
            const currentProperty = this.salesHistoryProperties[propertyIdx];
            if (Address1UnitNoZipCodeUtils.matches(currentProperty.address1UnitNoZipCode, property.address1UnitNoZipCode)) {
                foundIdx = Number(propertyIdx);
                break;
            }
        }

        if (foundIdx >= 0) {
            const replacePayload: IPropertyItemIndexed = {
                index: foundIdx,
                propertyItem: property
            };

            this.context.commit(Mutate.replaceProperty, replacePayload);
        } else {
            this.context.commit(Mutate.addProperty, property);
        }

        return foundIdx < 0;
    }

    /**
     * Action: Remove a sales history property by comp address.
     * @param compAddress
     * @returns true on removed, false on not found
     */
    @Action
    async removeProperty(compAddress: ICompAddress): Promise<boolean> {
        const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);

        let foundIdx = -1;
        for (const propertyIdx in this.salesHistoryProperties) {
            const currentProperty = this.salesHistoryProperties[propertyIdx];
            if (Address1UnitNoZipCodeUtils.matches(currentProperty.address1UnitNoZipCode, address1UnitNoZipCode)) {
                foundIdx = Number(propertyIdx);
                break;
            }
        }

        if (foundIdx >= 0) {
            this.context.commit(Mutate.deleteProperty, foundIdx);
        }

        return foundIdx >= 0;
    }

    /**
     * Action: Add or replace the given sales history selections using the given sales history selections.
     * @param selections
     * @returns true on add, false on replace
     */
    @Action
    async addOrReplaceSelections(selections: ISalesHistorySelections): Promise<boolean> {
        let foundIdx = -1;
        for (const selectionsIdx in this.salesHistorySelectionsArray) {
            const currentSelections = this.salesHistorySelectionsArray[selectionsIdx];
            if (Address1UnitNoZipCodeUtils.matches(currentSelections.address1UnitNoZipCode, selections.address1UnitNoZipCode)) {
                foundIdx = Number(selectionsIdx);
                break;
            }
        }

        if (foundIdx >= 0) {
            const replacePayload: ISelectionsItemIndexed = {
                index: foundIdx,
                selections: selections
            };
            this.context.commit(Mutate.replaceSelections, replacePayload);
        } else {
            this.context.commit(Mutate.addSelections, selections);
        }
        return foundIdx < 0;
    }

    /**
     * Action: Remove a sales history property by comp address.
     * @param compAddress
     * @returns true on removed, false on not found
     */
    @Action
    async removeSelections(compAddress: ICompAddress): Promise<boolean> {
        const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);

        let foundIdx = -1;
        for (const selectionsIdx in this.salesHistorySelectionsArray) {
            const currentSelections = this.salesHistorySelectionsArray[selectionsIdx];
            if (Address1UnitNoZipCodeUtils.matches(currentSelections.address1UnitNoZipCode, address1UnitNoZipCode)) {
                foundIdx = Number(selectionsIdx);
                break;
            }
        }

        if (foundIdx >= 0) {
            this.context.commit(Mutate.deleteSelections, foundIdx);
        }

        return foundIdx >= 0;
    }

    /**
     * Mutation: Adds the given property into the store's data.
     * Preference is to use the addOrReplaceProperty action.
     * @param property
     */
    @Mutation
    addProperty(property: ISalesHistoryProperty) {
        this.salesHistoryProperties.push(property);
    }

    /**
     * Mutation: Replace a sales history property in the store's data using the payload's index.
     * @param replacePayload
     */
    @Mutation
    replaceProperty(replacePayload: IPropertyItemIndexed) {
        this.salesHistoryProperties[replacePayload.index] = replacePayload.propertyItem;
    }

    /**
     * Mutation: Delete the sales history property at the given index.
     * @param index
     */
    @Mutation
    deleteProperty(index: number) {
        if (index < this.salesHistoryProperties.length) {
            this.salesHistoryProperties.splice(index, 1);
        }
    }

    @Mutation
    addSelections(selections: ISalesHistorySelections) {
        this.salesHistorySelectionsArray.push(selections);
    }

    /**
     * Mutation: Replace a sales history selections in the store's data using the payload's index.
     * @param replacePayload
     */
    @Mutation
    replaceSelections(replacePayload: ISelectionsItemIndexed) {
        this.salesHistorySelectionsArray[replacePayload.index] = replacePayload.selections;
    }

    /**
     * Mutation: Delete the sales history selections at the given index.
     * @param index
     */
    @Mutation
    deleteSelections(index: number) {
        if (index < this.salesHistorySelectionsArray.length) {
            this.salesHistorySelectionsArray.splice(index, 1);
        }
    }

    /**
     * Mutation: Reset all state.
     */
    @Mutation
    resetState() {
        this.salesHistoryProperties = [];
        this.salesHistorySelectionsArray = [];
    }
}
